/**
 * Admin Road Status page — official per-corridor clear/caution/closed.
 *
 * One row per active corridor. Setting a status is instant (no wizard):
 * pick the level, optionally type a short note ("landslide at Castle
 * Bruce"), Save. Riders see non-clear corridors as a banner at the top
 * of the "Where to?" card in the app within ~2 minutes (client cache).
 *
 * This is the official channel — crowd-sourced hazard pins live on the
 * separate Road Hazards page. Use BOTH for a major closure: hazard pin
 * for the map location, road status for the corridor-wide banner.
 */

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { AlertTriangle, Ban, CheckCircle2, TrafficCone } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

type Level = 'clear' | 'caution' | 'closed';

interface CorridorStatus {
  corridor_id: string;
  corridor_name: string;
  status: Level;
  note: string | null;
  updated_at: string | null;
}

const LEVELS: { value: Level; label: string; icon: typeof CheckCircle2; activeCls: string }[] = [
  {
    value: 'clear',
    label: 'Clear',
    icon: CheckCircle2,
    activeCls: 'bg-emerald-600 hover:bg-emerald-600 text-white border-emerald-600',
  },
  {
    value: 'caution',
    label: 'Caution',
    icon: AlertTriangle,
    activeCls: 'bg-amber-500 hover:bg-amber-500 text-white border-amber-500',
  },
  {
    value: 'closed',
    label: 'Closed',
    icon: Ban,
    activeCls: 'bg-red-600 hover:bg-red-600 text-white border-red-600',
  },
];

export default function RoadStatus() {
  const [rows, setRows] = useState<CorridorStatus[]>([]);
  const [loading, setLoading] = useState(true);
  // Per-corridor local edits (status + note) awaiting Save.
  const [edits, setEdits] = useState<Record<string, { status: Level; note: string }>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    const { data, error } = await supabase.rpc('get_road_status');
    if (error) {
      toast.error(`Failed to load road status: ${error.message}`);
    } else if (Array.isArray(data)) {
      setRows(data as CorridorStatus[]);
      setEdits({});
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const editFor = (r: CorridorStatus) =>
    edits[r.corridor_id] ?? { status: r.status, note: r.note ?? '' };

  const setEdit = (id: string, patch: Partial<{ status: Level; note: string }>) => {
    setEdits((prev) => {
      const row = rows.find((r) => r.corridor_id === id);
      const base = prev[id] ?? { status: row?.status ?? 'clear', note: row?.note ?? '' };
      return { ...prev, [id]: { ...base, ...patch } };
    });
  };

  const save = async (r: CorridorStatus) => {
    const edit = editFor(r);
    setSavingId(r.corridor_id);
    const { data, error } = await supabase.rpc('admin_set_road_status', {
      p_corridor_id: r.corridor_id,
      p_status: edit.status,
      p_note: edit.status === 'clear' ? null : edit.note.trim() || null,
    });
    setSavingId(null);
    const result = data as { ok?: boolean; error?: string } | null;
    if (error || !result?.ok) {
      toast.error(`Save failed: ${error?.message ?? result?.error ?? 'unknown error'}`);
      return;
    }
    toast.success(`${r.corridor_name} set to ${edit.status}. Riders see it within ~2 min.`);
    void load();
  };

  const isDirty = (r: CorridorStatus) => {
    const e = edits[r.corridor_id];
    if (!e) return false;
    return e.status !== r.status || e.note.trim() !== (r.note ?? '');
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <TrafficCone className="w-6 h-6 text-amber-500" />
          Road Status
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          The official word on each corridor. Anything not{' '}
          <span className="font-semibold text-emerald-600">Clear</span> shows as a
          banner in the rider app. Use Road Hazards for pin-point issues; use this
          for corridor-wide conditions (landslides, flooding, closures).
        </p>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => {
            const edit = editFor(r);
            return (
              <Card key={r.corridor_id}>
                <CardContent className="pt-5">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="min-w-[180px]">
                      <p className="font-semibold">{r.corridor_name}</p>
                      {r.updated_at ? (
                        <p className="text-[11px] text-gray-400">
                          updated {formatDistanceToNow(new Date(r.updated_at), { addSuffix: true })}
                        </p>
                      ) : (
                        <p className="text-[11px] text-gray-400">never changed</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {LEVELS.map((lvl) => {
                        const Icon = lvl.icon;
                        const activeNow = edit.status === lvl.value;
                        return (
                          <Button
                            key={lvl.value}
                            size="sm"
                            variant="outline"
                            className={activeNow ? lvl.activeCls : ''}
                            onClick={() => setEdit(r.corridor_id, { status: lvl.value })}
                          >
                            <Icon className="w-3.5 h-3.5 mr-1" />
                            {lvl.label}
                          </Button>
                        );
                      })}
                    </div>
                    {edit.status !== 'clear' ? (
                      <Input
                        className="flex-1 min-w-[220px]"
                        placeholder='Short note riders see, e.g. "Landslide at Castle Bruce. Expect delays."'
                        maxLength={200}
                        value={edit.note}
                        onChange={(e) => setEdit(r.corridor_id, { note: e.target.value })}
                      />
                    ) : null}
                    <Button
                      size="sm"
                      disabled={!isDirty(r) || savingId === r.corridor_id}
                      onClick={() => void save(r)}
                    >
                      {savingId === r.corridor_id ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
